import type { Rarity } from "@/domain/gems-game/domain/lib/Earning";
import { RARITY_COLOR } from "@/utils/rewards";
import { Box, darken } from "@mui/material";
import { lighten } from "@mui/material";

export const SEGMENT_LENGTHS: Record<Rarity, number> = {
  Common: 40,
  Uncommon: 25,
  Rare: 20,
  Epic: 12,
  Legendary: 3,
};

const buildGradient = (): string => {
  const rarities = Object.keys(SEGMENT_LENGTHS) as Rarity[];
  let cumulative = 0;
  const stops: string[] = [];
  const BLEND = 8;
  rarities.forEach((rarity, idx) => {
    const color = lighten(RARITY_COLOR[rarity], 0.1);
    const start = cumulative;
    const end = cumulative + SEGMENT_LENGTHS[rarity];

    const nextRarity = rarities[idx + 1];

    if (nextRarity) {
      const nextColor = lighten(RARITY_COLOR[nextRarity], 0.1);
      const blendStart = Math.max(start, end - BLEND);

      stops.push(`${color} ${start}%`, `${color} ${blendStart}%`);

      stops.push(`${color} ${blendStart}%`, `${nextColor} ${end}%`);
    } else {
      stops.push(`${color} ${start}%`, `${color} 100%`);
    }

    cumulative = end;
  });
  return `linear-gradient(90deg, ${stops.join(", ")})`;
};

const buildLegendaryOnlyGradient = (): string => {
  const legendaryColor = RARITY_COLOR.Legendary;
  const lightLegendary = lighten(legendaryColor, 0.5);
  const darkLegendary = darken(legendaryColor, 0.2);

  return `linear-gradient(90deg, ${lightLegendary} 0%, ${darkLegendary} 100%)`;
};

const FULL_GRADIENT = buildGradient();
const LEGENDARY_ONLY_GRADIENT = buildLegendaryOnlyGradient();

interface KeyProgressBarProps {
  shouldShowLegendaryOnly?: boolean;
}

export const KeyProgressBar = ({
  shouldShowLegendaryOnly,
}: KeyProgressBarProps) => {
  return (
    <Box
      sx={{
        height: "12px",
        borderRadius: 2,
        background: shouldShowLegendaryOnly
          ? LEGENDARY_ONLY_GRADIENT
          : FULL_GRADIENT,
      }}
    />
  );
};
